/**
 * Score parity gate for the PriorityRoadmap section.
 *
 * The roadmap tells a paid user "fixing X recovers N points". That claim is
 * only honest if a tool's per-category rubric actually adds up to the score we
 * show next to it. This module asserts that invariant:
 *
 *     sum(breakdown.score) === overallScore   AND   sum(maxScore) === 100
 *
 * It is a GATE, not a filter. If ANY of the six infrastructure tools fails,
 * the whole section is withheld. Computing a ceiling over the subset that
 * happens to pass would put a 4-tool ceiling next to a 6-tool unifiedScore —
 * a denominator mismatch that destroys the credibility the section exists to
 * create.
 *
 * The same assertion runs as a test over fixtures (parity.test.ts), including
 * degraded / empty / early-return paths. Happy-path-only coverage misses
 * exactly the bugs this was built to catch.
 */

import type { TierOneKey } from '@/lib/platform/derive-tool-scores'

/** Normalized rubric line item, shared across all six tools' native shapes. */
export interface RubricCategory {
  category: string
  score: number
  maxScore: number
  /**
   * False only when a scan could not verify the category (crawl-check). Such a
   * category leaves BOTH sides of the ratio; it does not score zero.
   */
  verifiable: boolean
}

/**
 * Why a tool failed the gate. The distinction matters downstream:
 *
 *  - `nonGradeable` — the tool ran but could not measure the site (withheld
 *    crawl, ai-readiness with both sub-tools down). Its numbers are absent,
 *    not wrong. The projection EXCLUDES it; it must never contribute a
 *    baseline, a ceiling, or a quick win.
 *  - `parityFailure` — the tool's rubric does not reconcile with its own
 *    score. Its numbers are actively untrustworthy, so nothing it reports can
 *    be used at all.
 *  - `missing` — the tool did not run.
 */
export type ParityFailureKind = 'nonGradeable' | 'parityFailure' | 'missing'

export interface ParityCheck {
  tool: TierOneKey
  ok: boolean
  /** Populated only when ok === false. */
  reason?: string
  failureKind?: ParityFailureKind
  categories: RubricCategory[]
}

export interface ParityResult {
  /** True only when all six tools pass. Anything else withholds the section. */
  ok: boolean
  checks: ParityCheck[]
  failures: string[]
}

/** The six tools whose rubrics feed the roadmap. */
export const PARITY_TOOLS: readonly TierOneKey[] = [
  'crawl',
  'robots',
  'llms',
  'readiness',
  'aeo',
  'schemaAudit',
] as const

const TOTAL_RUBRIC_POINTS = 100

/**
 * Structural view of a tool result. Deliberately loose: the six tools expose
 * their rubric under three different field names, and this module reads all of
 * them rather than forcing a migration across every tool at once.
 */
interface ToolResultLike {
  overallScore?: number | null
  scoreStatus?: 'graded' | 'withheld'
  scoreBreakdown?: {
    category: string
    score: number
    maxScore: number
    verifiable?: boolean
  }[]
  categories?: Record<string, { score: number; maxScore: number }>
}

/** Pull the rubric out of whichever shape a given tool ships. */
export function extractCategories(
  data: ToolResultLike | null | undefined
): RubricCategory[] | null {
  if (!data) return null

  if (Array.isArray(data.scoreBreakdown)) {
    return data.scoreBreakdown.map(b => ({
      category: b.category,
      score: b.score,
      maxScore: b.maxScore,
      // Only crawl-check carries verifiability; everywhere else every
      // category is always measured.
      verifiable: b.verifiable !== false,
    }))
  }

  if (data.categories && typeof data.categories === 'object') {
    return Object.entries(data.categories).map(([key, c]) => ({
      category: key,
      score: c.score,
      maxScore: c.maxScore,
      verifiable: true,
    }))
  }

  return null
}

/**
 * Check one tool. `overallScore` must equal the sum of its rubric, and the
 * rubric must span exactly 100 points.
 *
 * crawl-check is the one legitimate exception to a plain sum: it normalizes
 * over verifiable points only, so its total is
 * round(sum(verifiable score) / sum(verifiable maxScore) * 100). Its FULL
 * rubric (verifiable or not) must still span 100.
 */
export function checkToolParity(
  tool: TierOneKey,
  data: ToolResultLike | null | undefined
): ParityCheck {
  const categories = extractCategories(data)

  if (!categories || categories.length === 0) {
    return {
      tool,
      ok: false,
      reason: 'no rubric breakdown exposed',
      failureKind: 'missing',
      categories: [],
    }
  }

  // A withheld scan cannot anchor a ceiling: there is no honest score to
  // project from. Withhold the section rather than invent a baseline.
  if (data?.scoreStatus === 'withheld' || data?.overallScore == null) {
    return {
      tool,
      ok: false,
      reason:
        data?.scoreStatus === 'withheld'
          ? 'score withheld (tool could not measure the site)'
          : 'overallScore is null',
      failureKind: 'nonGradeable',
      categories,
    }
  }

  const possible = categories.reduce((sum, c) => sum + c.maxScore, 0)
  if (possible !== TOTAL_RUBRIC_POINTS) {
    return {
      tool,
      ok: false,
      reason: `rubric spans ${possible} points, expected ${TOTAL_RUBRIC_POINTS}`,
      failureKind: 'parityFailure',
      categories,
    }
  }

  const verifiable = categories.filter(c => c.verifiable)
  const earned = verifiable.reduce((sum, c) => sum + c.score, 0)
  const measurable = verifiable.reduce((sum, c) => sum + c.maxScore, 0)

  if (measurable === 0) {
    return {
      tool,
      ok: false,
      reason: 'no verifiable categories',
      failureKind: 'nonGradeable',
      categories,
    }
  }

  // Normalized when some categories were unverifiable, plain sum otherwise.
  // Both collapse to the same expression when everything is verifiable and
  // the rubric spans 100.
  const expected =
    measurable === TOTAL_RUBRIC_POINTS
      ? earned
      : Math.round((earned / measurable) * 100)

  if (data.overallScore !== expected) {
    return {
      tool,
      ok: false,
      reason: `overallScore ${data.overallScore} != rubric total ${expected}`,
      failureKind: 'parityFailure',
      categories,
    }
  }

  // Guards the bound that replaced crawl-check's Math.min clamp. A category
  // scoring above its own maxScore is a rubric bug, and must fail the gate
  // rather than be silently clipped into a plausible number.
  const overScored = categories.find(c => c.score > c.maxScore)
  if (overScored) {
    return {
      tool,
      ok: false,
      reason: `category "${overScored.category}" scored ${overScored.score} of ${overScored.maxScore}`,
      failureKind: 'parityFailure',
      categories,
    }
  }

  return { tool, ok: true, categories }
}

/**
 * Run the gate across all six tools. `ok` is true only when every one passes.
 */
export function checkParity(
  results: Partial<Record<TierOneKey, ToolResultLike | null>>
): ParityResult {
  const checks = PARITY_TOOLS.map(tool => checkToolParity(tool, results[tool]))
  const failures = checks.filter(c => !c.ok).map(c => `${c.tool}: ${c.reason}`)

  return { ok: failures.length === 0, checks, failures }
}
